import "../styles/tokens.css";
import "../styles/base.css";
import "../styles/components.css";
import "../styles/responsive.css";

import { localePath, messages } from "../i18n";
import { buildSiteFooter } from "./footer";
import { buildArticleLocaleControl, buildSiteHeader } from "./header";
import type { Locale } from "../i18n/types";
import type { ArticleMetadata } from "../shared/articles";

const root = document.querySelector<HTMLElement>("#app");
if (!root) throw new Error("Missing #app root");

const state = {
  locale: (document.documentElement.lang === "en" ? "en" : "zh-TW") as Locale
};

const articleListCopy = {
  en: {
    title: "IP Knowledge",
    subtitle:
      "Learn how IP addresses, network environments, proxy services, browser signals, and risk-detection systems work."
  },
  "zh-TW": {
    title: "科普文章",
    subtitle: "了解 IP、網路環境、代理偵測、瀏覽器指紋與平台風控的基本原理。"
  }
} as const;

function el<T extends HTMLElement>(tag: string, className?: string, text?: string): T {
  const element = document.createElement(tag) as T;
  if (className) element.className = className;
  if (text) element.textContent = text;
  return element;
}

function buildHeader(copy: typeof messages["zh-TW"]) {
  const languageControl = buildArticleLocaleControl(
    copy,
    state.locale,
    state.locale === "en" ? "/articles/" : "/en/articles/"
  );

  return buildSiteHeader(copy, state.locale, languageControl, {
    articlesHref: state.locale === "en" ? "/en/articles/" : "/articles/",
    articlesLabel: state.locale === "en" ? "IP Knowledge" : "科普文章",
    esimLabel: state.locale === "en" ? "Caylet eSIM" : "自研 eSIM"
  });
}

// footer logic replaced by buildSiteFooter

function renderArticlesList() {
  const copy = messages[state.locale];
  const pageCopy = articleListCopy[state.locale === "en" ? "en" : "zh-TW"];
  root!.innerHTML = "";

  const frag = document.createDocumentFragment();
  frag.append(buildHeader(copy));

  const main = el("main");
  main.id = "main-content";

  const section = el("section", "articles-list-section");

  // Modern Hero Header
  const hero = el("div", "articles-hero");
  const badge = el("div", "articles-hero-badge");
  badge.innerHTML = `
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
    </svg>
    <span>${state.locale === "en" ? "Caylet Knowledge Hub" : "Caylet 網路安全知識庫"}</span>
  `;
  const title = el("h1", "articles-hero-title", pageCopy.title);
  const subtitle = el("p", "articles-hero-subtitle", pageCopy.subtitle);
  hero.append(badge, title, subtitle);
  section.append(hero);

  // Read articles data injected into HTML
  let articles: ArticleMetadata[] = [];
  try {
    const dataEl = document.getElementById("articles-data");
    if (dataEl && dataEl.textContent) {
      articles = JSON.parse(dataEl.textContent);
    }
  } catch {
    // Ignore parse error
  }

  if (articles.length === 0) {
    const empty = el("div", "empty-state article-empty-state", copy.articles?.emptyState);
    const backBtn = el<HTMLAnchorElement>(
      "a",
      "primary-button article-inline-button",
      copy.articles?.backToTool
    );
    backBtn.href = localePath(state.locale);
    section.append(empty, backBtn);
  } else {
    const grid = el("div", "articles-grid");
    articles.forEach((article) => {
      // Retain .result-card for e2e test assertion compatibility
      const card = el<HTMLAnchorElement>("a", "article-card result-card article-card-link");
      card.href = `${state.locale === "en" ? "/en" : ""}/articles/${article.slug}/`;

      // Top Meta: Category Badge + Date
      const cardTop = el("div", "article-card-top");
      const categoryBadge = el(
        "span",
        "article-category-badge",
        article.category || (state.locale === "en" ? "Network" : "網路安全")
      );

      const dateStr =
        state.locale === "en"
          ? new Date(article.publishedAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
              timeZone: "UTC"
            })
          : new Date(article.publishedAt).toLocaleDateString("zh-TW", {
              year: "numeric",
              month: "numeric",
              day: "numeric",
              timeZone: "UTC"
            });
      const dateEl = el("span", "article-card-date", dateStr);
      cardTop.append(categoryBadge, dateEl);

      // Card Title
      const cardTitle = el("h2", "article-card-title", article.title);

      // Card Summary
      const summary = el("p", "article-card-summary", article.summary);

      // Card Footer with "Read full article"
      const footer = el("div", "article-card-footer");
      const readMore = el(
        "span",
        "article-read-more",
        state.locale === "en" ? "Read full analysis" : "閱讀完整深度解析"
      );
      readMore.innerHTML += `
        <svg class="article-arrow-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="5" y1="12" x2="19" y2="12"></line>
          <polyline points="12 5 19 12 12 19"></polyline>
        </svg>
      `;
      footer.append(readMore);

      card.append(cardTop, cardTitle, summary, footer);
      grid.append(card);
    });
    section.append(grid);
  }

  main.append(section);

  const { footer, modals } = buildSiteFooter(copy);
  frag.append(main);
  frag.append(footer, ...modals);

  root!.append(frag);
}

renderArticlesList();
